/* eslint-disable import/extensions */
import { useQueryParam } from '@hooks/useQueryParams';
import { baseErrorHandler } from '@queries/baseErrorHandler';
import { $api } from '@service/RequestService';
import { DATE_FORMAT } from '@utils/constants';
import { getDebitsBody } from '@utils/helper/getSearchAndDownloadBodyAllTables';
import { encodeString, isEmptyObj } from '@utils/index';
import { DebtList } from '@utils/models/DebtList';
import { Pagination } from '@utils/models/Pagination';
import dayjs from 'dayjs';
import { useInfiniteQuery, useQuery } from 'react-query';

import { SearchParamsArrearage } from '../Arrearage';
import { endPoints } from '../utils/constants/endPoints';
import { queryKeys } from '../utils/constants/queryKeys';
import { ensureAllTypesExist } from '../utils/helpers/fill-data';
import { DebtStatisticsType, GraphicSumPaymentType, GraphicTableType } from '../utils/models';

export function useDebitList(searchObj: SearchParamsArrearage) {
  return useQuery<Pagination<DebtList>>(
    [queryKeys.DEBIT_LIST, searchObj.page, JSON.stringify(searchObj)],
    async () => {
      const res = await $api.post(endPoints.DEBT, getDebitsBody(searchObj));

      return res?.data?.data;
    },
    {
      enabled: !!searchObj.page && !!searchObj.size
    }
  );
}

export const useSearchDebt = (search: string) =>
  useInfiniteQuery<Pagination<DebtList>>(
    [queryKeys.DEBIT_LIST, search],
    async ({ pageParam = 1 }) => {
      const res = await $api.post('/v1/debt', { page: pageParam, size: 10, search });

      return res?.data?.data;
    },
    {
      getNextPageParam: (lastPage, page) => {
        if (page.length < lastPage?.totalPages) {
          return page.length + 1;
        }
        return undefined;
      },
      enabled: !!search,
      staleTime: 3600
    }
  );

export function useDebtStatistics(size: number) {
  const { searchParams } = useQueryParam();

  // @ts-expect-error
  delete searchParams?.page;

  const initial: DebtStatisticsType = {
    count: 0,
    // @ts-expect-error
    prepayment: null,
    // @ts-expect-error
    monthlyPayment: null
  };

  const { data = initial, ...arg } = useQuery<DebtStatisticsType>(
    ['debt-statistics', searchParams, size],
    async () => {
      const { data } = await $api.post('/v1/debt/sum', getDebitsBody(searchParams as never));

      return { ...data?.data };
    },
    {
      retry: false,
      refetchOnWindowFocus: false
    }
  );

  return { ...data, ...arg };
}

export function useGetMonthlyPayment() {
  const { searchParams } = useQueryParam<any, any>();
  const today = dayjs().format(DATE_FORMAT);

  return useQuery<Pagination<GraphicTableType>>({
    queryKey: ['monthly-payment', searchParams],
    queryFn: async () => {
      const res = await $api.post(`/v1/monthly-payment`, {
        ...searchParams,
        page: searchParams?.page || 1,
        size: searchParams?.size || 10,
        startDate: searchParams?.startDate || today,
        finishDate: searchParams?.finishDate || today,
        ...(searchParams?.responsibleIds && {
          responsibleIds: Array.isArray(searchParams.responsibleIds)
            ? searchParams.responsibleIds
            : [searchParams.responsibleIds]
        }),
        ...(searchParams?.houseIds && {
          houseIds: Array.isArray(searchParams.houseIds) ? searchParams.houseIds : [searchParams.houseIds]
        }),
        ...(searchParams?.buildingIds && {
          buildingIds: Array.isArray(searchParams.buildingIds) ? searchParams.buildingIds : [searchParams.buildingIds]
        }),
        ...(searchParams?.currencies && {
          currencies: Array.isArray(searchParams.currencies) ? searchParams.currencies : [searchParams.currencies]
        })
      });

      return res.data.data;
    }
  });
}

export function useGetMonthlyPayymentSum() {
  const { searchParams } = useQueryParam<any, any>();
  const today = dayjs().format(DATE_FORMAT);

  return useQuery<GraphicSumPaymentType>({
    queryKey: ['monthly-payment-sum', searchParams],
    queryFn: async () => {
      const res = await $api.post(`/v1/monthly-payment/sum`, {
        ...searchParams,
        page: searchParams?.page || 1,
        size: searchParams?.size || 10,
        startDate: searchParams?.startDate || today,
        finishDate: searchParams?.finishDate || today,
        ...(searchParams?.responsibleIds && {
          responsibleIds: Array.isArray(searchParams.responsibleIds)
            ? searchParams.responsibleIds
            : [searchParams.responsibleIds]
        }),
        ...(searchParams?.houseIds && {
          houseIds: Array.isArray(searchParams.houseIds) ? searchParams.houseIds : [searchParams.houseIds]
        }),
        ...(searchParams?.buildingIds && {
          buildingIds: Array.isArray(searchParams.buildingIds) ? searchParams.buildingIds : [searchParams.buildingIds]
        }),
        ...(searchParams?.currencies && {
          currencies: Array.isArray(searchParams.currencies) ? searchParams.currencies : [searchParams.currencies]
        })
      });

      const { data } = res.data;

      return {
        ...data,
        amount: {
          ...data.amount,
          currencies: { USD: data?.amount?.currencies?.USD || 0, UZS: data?.amount?.currencies?.UZS || 0 },
          total: data?.amount?.total || 0,
          types: ensureAllTypesExist(data.amount?.types)
        },
        payment: {
          ...data.payment,
          currencies: { USD: data?.payment?.currencies?.USD || 0, UZS: data?.payment?.currencies?.UZS || 0 },
          total: data?.payment?.total || 0,
          types: ensureAllTypesExist(data.payment?.types)
        }
      };
    }
  });
}
